import { useRef, useState } from "react";
import { createDocument, jobStatus } from "./api";
import { uploadFile } from "./firebase";

// Formaten backenden kan läsa (AA-2, parsing.py). Listan står här också för att
// filväljaren ska säga nej innan filen laddas upp — en fil som avvisas först i
// arbetartråden har redan kostat uppladdningstid och en jobbrad.
const ACCEPT = ".pdf,.docx,.txt,.md";

const TERMINAL = ["ready", "failed"];

// Hur många gånger status frågas innan portalen ger upp.
//
// Utan tak pollar vyn i evighet om ett jobb aldrig når terminal status —
// arbetartråden dog, processen startade om mitt i. Jobbet står då kvar på
// `parsing` för alltid, och det enda som händer är att portalen frågar var
// annan sekund resten av dagen.
//
// Hundrafemtio gånger två sekunder är fem minuter. Ett normalt skoldokument tar
// sekunder; ett som inte är klart efter fem minuter är inte långsamt, det är
// fast. Att säga det är mer användbart än att fortsätta snurra.
const MAX_POLLS = 150;

// Högsta antal filer i en och samma uppladdning.
//
// **Inte en minnesgräns.** Servern håller aldrig mer än
// RAG_INGEST_CONCURRENCY (3) filers bytes samtidigt: semaforen tas innan
// filen hämtas ur lagringen, så köade jobb väntar utan att ockupera minne.
// Klienten håller bara File-referenser, som är pekare till disk och inte
// inlästa data. Tjugo rader kostar alltså inte tjugo gånger 25 MB någonstans.
//
// Gränsen finns av två andra skäl. Den håller skadeområdet nere om någon
// råkar markera en hel mapp — femhundra rader vore femhundra jobbrader och
// femhundra objekt i lagringen. Och den gör ett sådant misstag *synligt*: man
// får ett besked i stället för en tyst kvart av uppladdning.
//
// Tjugo och inte tio: den naturliga första omgången är de tio dokument
// eleverna oftast frågar efter (etapp 3), och en gräns som ligger exakt på det
// naturliga arbetssättet är en gräns man slår i utan att ha gjort fel.
const MAX_FILES = 20;

/**
 * Titelförslag ur filnamnet, utan ändelse.
 *
 * Titeln går rakt in i kontextprefixet ("Dokument: ..."), som var halva skälet
 * till att rad 19 gick från plats 10 till plats 1 i etapp 2. Fältet är därför
 * det viktigaste i formuläret.
 *
 * Förifyllt men redigerbart: den som inte orkar skriva får ändå något bättre än
 * ingenting, och den som orkar ser direkt att det går att ändra. Backenden har
 * ett bättre förslag när fältet lämnas tomt — dokumentets egen förstarubrik,
 * `parsing.suggest_title` — men ett tomt obligatoriskt fält blir ifyllt med
 * minsta möjliga möda, och då är ett filnamn bättre än ett tomrum.
 */
function titleFrom(file) {
  return file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim();
}

let nextId = 1;

export default function Upload({ user, schoolId, onDone }) {
  // En rad per dokument: egen fil, egen titel, eget datum, egen status.
  const [rows, setRows] = useState([]);
  // Om en tom Browse-rad visas. Skolans IT har sällan alla filer i samma mapp —
  // ordningsregler och läsårstider ligger på olika ställen — så en rad i taget
  // är normalfallet. Multi-select i en enskild Browse fungerar ändå, för den
  // som HAR dem samlade ska slippa klicka fem gånger.
  const [picking, setPicking] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const inputRef = useRef(null);

  function addFiles(fileList) {
    const chosen = Array.from(fileList || []);
    if (!chosen.length) return;

    const room = MAX_FILES - rows.length;
    const taken = chosen.slice(0, Math.max(room, 0));
    setNotice(chosen.length > taken.length
      ? `Högst ${MAX_FILES} filer per uppladdning. ${chosen.length - taken.length} `
        + "togs inte med — ladda upp dem i en andra omgång."
      : null);

    setRows((current) => [
      ...current,
      ...taken.map((file) => ({
        id: nextId++,
        file,
        title: titleFrom(file),
        validUntil: "",
        state: null,
        detail: null,
      })),
    ]);
    setPicking(false);
    // Nollställ inputen, annars går samma fil inte att välja igen.
    if (inputRef.current) inputRef.current.value = "";
  }

  function patch(id, changes) {
    setRows((current) => current.map((r) => (r.id === id ? { ...r, ...changes } : r)));
  }

  function remove(id) {
    // Filtreringen görs utanför uppdateraren. En setState-uppdaterare kan
    // anropas två gånger i StrictMode, och en sidoeffekt inuti den körs då
    // också två gånger — här vore det harmlöst, men det är inte en vana värd
    // att ha i en fil där nästa sidoeffekt kanske inte är det.
    const left = rows.filter((r) => r.id !== id);
    setRows(left);
    if (!left.length) setPicking(true);
    setNotice(null);
  }

  async function runOne(row) {
    try {
      patch(row.id, { state: "laddar upp", detail: null });
      const storagePath = await uploadFile(user, schoolId, row.file);

      patch(row.id, { state: "startar" });
      const { job_id } = await createDocument(user, {
        storage_path: storagePath,
        filename: row.file.name,
        title: row.title.trim(),
        valid_until: row.validUntil || null,
        // AA-6.3. Gemensam för hela uppladdningen: frågan gäller
        // personuppgifter i allmänhet, inte den enskilda filen.
        no_personal_data: confirmed,
      });

      for (let poll = 0; poll < MAX_POLLS; poll += 1) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const job = await jobStatus(user, job_id);
        patch(row.id, {
          state: describe(job),
          detail: job.status === "failed" ? job.reason : null,
        });
        if (TERMINAL.includes(job.status)) return job.status === "ready";
      }

      patch(row.id, {
        state: "vet inte",
        detail: "Filen är mottagen men inläsningen blev inte klar inom fem "
                + "minuter. Ladda om sidan för att se om den gick igenom.",
      });
      return false;
    } catch (caught) {
      // Felet stannar på raden. Nästa fil körs ändå — en trasig PDF mitt i en
      // omgång ska inte kosta de nio andra.
      patch(row.id, { state: "misslyckades", detail: caught.message });
      return false;
    }
  }

  async function submit(event) {
    event.preventDefault();
    if (!ready || busy) return;

    setBusy(true);
    setNotice(null);

    const done = [];
    for (const row of rows) {
      if (await runOne(row)) done.push(row.id);
      onDone();
    }
    setBusy(false);

    // Lyckade rader tas bort, misslyckade ligger kvar med sitt felmeddelande.
    //
    // Att låta allt ligga kvar hade betytt att ett andra försök laddade upp de
    // redan klara en gång till: dedupen på innehållshash hade hindrat dubbla
    // dokument, men inte dubbla storage-objekt och jobbrader. Ett omförsök ska
    // gälla det som inte gick igenom.
    const left = rows.filter((r) => !done.includes(r.id));
    setRows(left);
    if (!left.length) {
      setPicking(true);
      setConfirmed(false);
    } else {
      setNotice(`${done.length} av ${rows.length} klara. Raderna nedan gick inte `
                + "igenom — rätta eller ta bort dem och försök igen.");
    }
  }

  const full = rows.length >= MAX_FILES;
  const ready = rows.length > 0 && confirmed && rows.every((r) => r.title.trim());

  return (
    <form className="card" onSubmit={submit}>
      <h2>Ladda upp dokument</h2>

      <p className="hint">
        Har du dokumentet i Word? Ladda hellre upp <code>.docx</code> än PDF —
        Ask Aerro läser rubrikerna och hittar rätt avsnitt bättre då.
      </p>

      {rows.length > 0 && (
        <ul className="rows">
          {rows.map((row) => (
            <li key={row.id}>
              <div className="rowhead">
                <span className="filename">{row.file.name}</span>
                {!busy && (
                  <button type="button" className="dismiss"
                          onClick={() => remove(row.id)}>Ta bort</button>
                )}
                {row.state && (
                  <span className={row.detail ? "state failed" : "state"}>
                    {row.state}
                  </span>
                )}
              </div>

              <label>
                Titel
                <input type="text" value={row.title} disabled={busy} required
                       onChange={(e) => patch(row.id, { title: e.target.value })} />
              </label>

              <label>
                Gäller till och med <span className="optional">(valfritt)</span>
                <input type="date" value={row.validUntil} disabled={busy}
                       onChange={(e) => patch(row.id, { validUntil: e.target.value })} />
              </label>

              {row.detail && <p className="reason">{row.detail}</p>}
            </li>
          ))}
        </ul>
      )}

      {rows.length > 0 && !busy && (
        <p className="hint">
          Titeln visas för eleverna och används när Ask Aerro letar. Den är
          ifylld från filnamnet — <strong>skriv om den</strong> så att den heter
          som dokumentet, inte som filen.
        </p>
      )}

      {picking && !full && (
        <label>
          {rows.length ? "Nästa fil" : "Fil"}
          <input ref={inputRef} type="file" accept={ACCEPT} multiple disabled={busy}
                 onChange={(e) => addFiles(e.target.files)} />
          {rows.length === 0 && (
            <span className="hint">
              Ligger flera dokument i samma mapp kan du markera dem allihop.
            </span>
          )}
        </label>
      )}

      {!picking && !full && !busy && (
        <button type="button" className="secondary" onClick={() => setPicking(true)}>
          Lägg till en fil till
        </button>
      )}

      {full && <p className="hint">Högst {MAX_FILES} filer per uppladdning.</p>}

      {rows.length > 0 && (
        <label className="checkbox">
          <input type="checkbox" checked={confirmed} disabled={busy}
                 onChange={(e) => setConfirmed(e.target.checked)} />
          <span>
            {rows.length > 1 ? "Dokumenten innehåller" : "Dokumentet innehåller"}{" "}
            <strong>inga personuppgifter om enskilda elever</strong>. Ladda inte
            upp klasslistor, elevhälsoplaner, åtgärdsprogram eller
            betygsunderlag.
          </span>
        </label>
      )}

      {notice && <p className="hint notice">{notice}</p>}

      {rows.length > 0 && (
        <button type="submit" disabled={!ready || busy}>
          {busy ? "Arbetar…"
                : rows.length > 1 ? `Ladda upp ${rows.length} filer` : "Ladda upp"}
        </button>
      )}
    </form>
  );
}

function describe(job) {
  if (job.status === "queued") return "i kö";
  if (job.status === "parsing") return "läser filen";
  if (job.status === "embedding") return "indexerar";
  if (job.status === "ready") return job.reason || `klart — ${job.chunks} textstycken`;
  if (job.status === "failed") return "misslyckades";
  return job.status;
}
